import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import {
  fetchLabOrder,
  updateLabOrder,
  fetchLabAttachments,
  addLabAttachment,
  deleteLabAttachment,
  clearLabOrder,
} from '../store/labOrdersSlice'
import { fetchVeterinarians } from '../store/settingsSlice'
import { LabOrderDetailHeader } from '../components/lab-orders/LabOrderDetailHeader'
import { OrderInfo } from '../components/lab-orders/OrderInfo'
import { LabResults } from '../components/lab-orders/LabResults'
import { LabAttachments } from '../components/lab-orders/LabAttachments'

export function LabOrderDetailPage() {
  const { labOrderId } = useParams<{ labOrderId: string }>()
  const dispatch = useAppDispatch()
  const { order, attachments, loading } = useAppSelector((s) => s.labOrders)
  const veterinarians = useAppSelector((s) => s.settings.veterinarians)

  useEffect(() => {
    if (labOrderId) {
      dispatch(fetchLabOrder(labOrderId))
      dispatch(fetchLabAttachments(labOrderId))
      dispatch(fetchVeterinarians())
    }
    return () => {
      dispatch(clearLabOrder())
    }
  }, [dispatch, labOrderId])

  const handleUpdateStatus = async (newStatus: string) => {
    if (!labOrderId) return
    const data: Record<string, string> = { status: newStatus }
    if (newStatus === 'Results Received') {
      data.date_completed = new Date().toISOString().slice(0, 10)
    }
    await dispatch(updateLabOrder({ id: labOrderId, data }))
  }

  const handleSaveResults = async (data: { results: string; reference_ranges: string; abnormal_flags: string }) => {
    if (!labOrderId) return
    await dispatch(updateLabOrder({ id: labOrderId, data }))
  }

  const handleMarkReviewed = async (vetId: string) => {
    if (!labOrderId) return
    await dispatch(updateLabOrder({
      id: labOrderId,
      data: {
        status: 'Reviewed',
        reviewed_by: vetId,
        reviewed_at: new Date().toISOString(),
      },
    }))
  }

  const handleUploadAttachment = async (data: { lab_order_id: string; filename: string; url: string; file_type: string; size: number }) => {
    await dispatch(addLabAttachment(data))
  }

  const handleDeleteAttachment = async (attachmentId: string) => {
    await dispatch(deleteLabAttachment(attachmentId))
  }

  if (loading || !order) {
    return (
      <div className="p-6 max-sm:p-3" data-testid="lab-order-detail-page">
        <p className="text-[13px] text-text-muted">Loading lab order...</p>
      </div>
    )
  }

  return (
    <div className="p-6 max-sm:p-3" data-testid="lab-order-detail-page">
      <LabOrderDetailHeader order={order} />

      <div className="flex flex-col gap-4 mt-4">
        <OrderInfo order={order} onUpdateStatus={handleUpdateStatus} />
        <LabResults
          order={order}
          onSaveResults={handleSaveResults}
          onMarkReviewed={handleMarkReviewed}
          veterinarians={veterinarians}
        />
        <LabAttachments
          attachments={attachments}
          labOrderId={order.id}
          onUpload={handleUploadAttachment}
          onDelete={handleDeleteAttachment}
        />
      </div>
    </div>
  )
}
